import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useQuizlets } from '@/queries/quizlets';
import { CircularLoader } from '@/components/CircularLoader';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/queries/query-keys';
import styles from './quizlets.module.scss';

export const Route = createFileRoute('/analytics/quizlets')({
  beforeLoad: async ({ context }: { context: any }) => {
    if (context.authLoading) return;
    const user = context.user;
    if (!user) {
      throw redirect({ to: '/login' });
    }

    const queryClient = context.queryClient;

    // Check admin status similar to useAdminStatus hook
    try {
      const data = await queryClient.fetchQuery({
        queryKey: queryKeys.admin.status(),
        queryFn: async () => {
          const { data, error } = await supabase
            .from('user_profiles')
            .select('permission_level')
            .eq('user_id', user.id)
            .single();

          if (error) throw error;

          return {
            isAdmin: data?.permission_level?.admin === true,
          };
        },
        staleTime: 5 * 60 * 1000,
      });

      if (!data.isAdmin) {
        throw redirect({ to: '/analytics' });
      }
    } catch (error) {
      // If validation fails or redirects, let it propagate
      if (error instanceof Response || (error as any)?.isRedirect) {
        throw error;
      }
      // Fallback for error: deny access
      throw redirect({ to: '/analytics' });
    }
  },
  component: AnalyticsQuizletSelector,
});

function AnalyticsQuizletSelector() {
  const { data: quizlets, isLoading, error } = useQuizlets();

  if (isLoading) {
    return (
      <div className={styles.selector__loadingContainer}>
        <CircularLoader />
        <p className={styles.selector__loadingText}>Loading your quizlets...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.selector__errorContainer}>
        <div className={styles.selector__errorContent}>
          <h2 className={styles.selector__errorTitle}>Error Loading Quizlets</h2>
          <p className={styles.selector__errorMessage}>{error.message}</p>
          <Link to="/analytics">
            <motion.button
              className={styles.selector__backButton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Back to Analytics Dashboard
            </motion.button>
          </Link>
        </div>
      </div>
    );
  }

  const isEmpty = !quizlets || quizlets.length === 0;

  return (
    <div className={styles.selector}>
      <div className={styles.selector__header}>
        <div>
          <div className={styles.selector__breadcrumb}>
            <Link to="/analytics" className={styles.selector__breadcrumbLink}>
              Analytics
            </Link>
            <span className={styles.selector__breadcrumbSeparator}>/</span>
            <span className={styles.selector__breadcrumbCurrent}>By Quizlet</span>
          </div>
          <h1 className={styles.selector__title}>Quizlet Analytics</h1>
          <p className={styles.selector__subtitle}>
            Select a quizlet to view detailed performance analytics
          </p>
        </div>
      </div>

      <div className={styles.selector__content}>
        {isEmpty ? (
          <div className={styles.selector__emptyState}>
            <div className={styles.selector__emptyIcon}>📚</div>
            <h2 className={styles.selector__emptyTitle}>No Quizlets Yet</h2>
            <p className={styles.selector__emptyText}>
              Create your first quizlet to start tracking your learning progress
            </p>
            <Link to="/">
              <motion.button
                className={styles.selector__createButton}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Create Quizlet
              </motion.button>
            </Link>
          </div>
        ) : (
          <div className={styles.selector__grid}>
            {quizlets.map((quizlet) => (
              <Link
                key={quizlet.id}
                to="/analytics/$quizletId"
                params={{ quizletId: quizlet.id }}
                className={styles.selector__card}
              >
                <motion.div
                  className={styles.selector__cardContent}
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className={styles.selector__cardHeader}>
                    <h3 className={styles.selector__cardTitle}>{quizlet.name}</h3>
                    <p className={styles.selector__cardDescription}>
                      Created {new Date(quizlet.created_at).toLocaleDateString()}
                      {quizlet.last_practiced_at && (
                        <> • Last practiced {new Date(quizlet.last_practiced_at).toLocaleDateString()}</>
                      )}
                    </p>
                  </div>

                  <div className={styles.selector__cardStats}>
                    <div className={styles.selector__stat}>
                      <span className={styles.selector__statValue}>
                        {quizlet.questions?.length || 0}
                      </span>
                      <span className={styles.selector__statLabel}>Questions</span>
                    </div>
                    <div className={styles.selector__stat}>
                      <span className={styles.selector__statValue}>
                        {quizlet.practice_count || 0}
                      </span>
                      <span className={styles.selector__statLabel}>Practices</span>
                    </div>
                  </div>

                  <div className={styles.selector__cardFooter}>
                    <span className={styles.selector__viewAnalytics}>
                      View Analytics →
                    </span>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
